import * as _ from 'lodash'
import AnnotatedAbstract from './AnnotatedAbstract'
import { FOLLOW_UP_SENSES, phraseRelated } from './DocSearch'
import { Annotation, measureValue } from './Annotations'
import { toRangePart } from 'util/quantifier'
import { emptyUnit } from './extraction'
import { Extraction } from 'services/extract/types'

const SOURCE_PRECEDENCE = { user: 1 }

/**
 * Logic for automatic extraction
 */
export default class Extractor {

	static extract(extraction: Extraction, annotated: AnnotatedAbstract) {
		const annots = annotated.annotations.valueOf()
		// let searchedItvns = annotated.referencedTerms.interventions
		// searchedItvns = _.sortBy(searchedItvns, term => isPlacebo(term) ? 1 : 0)

		const allAbstractItvns = annots.filter(a => a.type == 'intervention')
		let abstractItvns = allAbstractItvns.slice()
		let abstractOutcomes = annots.filter(a => a.type == 'outcome')

		const assignedAnnotation = new Set()

		let updated = false

		const itvnGrpIds = []
		const itvns = []
		const substituteTerms = []

		// for all prefilled interventions/groups, assign related data
		for (const group of extraction.groups || []) {
			for (const intervention of group.interventions || []) {
				itvnGrpIds.push(group.id)
				itvns.push(intervention)
				const phrase = intervention.name?.term
				if (phrase) {
					const relatedAnnotations = abstractItvns.filter(a => phraseRelated(phrase, a.terms))
					const annot = this.chooseIntervention(relatedAnnotations, annotated)
					if (annot) {
						// assign term found in abstract to extracted intervention
						intervention.name.a = annot
						const text = (annot.text || '').trim()
						if ((intervention.name.val || '').trim().toLowerCase() != text.toLowerCase()) {
							substituteTerms.push([intervention.name, text])
						}
						assignedAnnotation.add(annot)
						abstractItvns = _.without(abstractItvns, annot)
						const firstTreatment = intervention.treatments[0]
						if (!firstTreatment.measure.val && annot.measures && annot.measures.length) {
							// just take the first related measure
							firstTreatment.measure.a = annot.measures[0]
							firstTreatment.measure.val = measureValue(annot.measures[0])
							updated = true
						}
						if (!group.n.val && annot.groupn) {
							group.n.a = annot.groupn
							group.n.val = annot.groupn.captured || annot.groupn.text
							updated = true
						}
					}
				}
			}
		}

		let timepoints = annots.filter(a => a.senses && a.senses.some(s => FOLLOW_UP_SENSES.includes(s)))
		let timepointAnnot
		let timepoint
		if (timepoints.length == 1) {
			const t = timepoints[0]
			if (t.measures && t.measures.length) {
				timepointAnnot = t
				timepoint = measureValue(t.measures[0])
			}
		}

		// assign outcome values
		for (const outcome of extraction.outcomes || []) {
			if (!outcome.timepoint && timepoint) {
				outcome.timepoint = { val: timepoint, a: timepointAnnot }
			}
			if (outcome.name.term) {
				const relatedOutcomes = abstractOutcomes.filter(a => phraseRelated(outcome.name.term, a.terms))
				const o = this.chooseOutcome(relatedOutcomes, annotated)
				if (o) {
					// assign term found in abstract to extracted outcome
					outcome.name.a = o
					const text = (o.text || '').trim()
					if ((outcome.name.val || '').trim().toLowerCase() != text.toLowerCase()) {
						substituteTerms.push([outcome.name, text])
					}
					abstractOutcomes = _.without(abstractOutcomes, o)
					const measures = o.measures
					if (measures && measures.length) {
						const [pcts, nums] = _.partition(measures, v => /%$/.test(measureValue(v)))
						// if we have both pcts and nums, assume they are reflecting the same measurements.
						// choose pcts only if there are more values
						const values = pcts.length > nums.length ? pcts : nums

						let itvnOrder = itvnGrpIds
						// infer the order of interventions from last made references
						const order = this.inferInterventionOrder(o, itvns, allAbstractItvns)
						if (order) {
							itvnOrder = Array.from(order.values()).map(i => itvnGrpIds[i])
						}
						itvnOrder.forEach((id, i) => {
							let v = outcome.cells[id]
							if (v == null && values[i]) {
								const a = values[i]
								_.set(outcome.cells, [id, 'a'], a)
								_.set(outcome.cells, [id, 'val'], measureValue(a))
								if (!['Binary', 'Participant'].includes(outcome.type)) {
									if (a.parsed?.quantifier && !outcome.unit) {
										// assign the outcome unit if our value has a unit
										const unit = toRangePart(a.parsed.quantifier.High?.Units).Unit
										if (!emptyUnit(unit)) outcome.unit = unit
									}
								}
							}
							updated = true
						})
					}
				}
			}
		}

		// substitute text after the fact so removing the assigned term object does not interfere with correlations
		substituteTerms.forEach(([obj, text]) => {
			obj.val = text
			obj.term = null
		})

		return updated
	}

	/**
	 * @param lists a list of position sorted references of items that are related to source items
	 * @param sourceItems a list of source items whose reference order we are inferring
	 */
	private static inferPriorOrder(lists, sourceItems, itemsRelated) {
		for (const list of lists) {
			const itemSrc = new Map<any, any[]>()
			const remaining = new Set(sourceItems)
			for (const item of list) {
				for (const srcItem of sourceItems) {
					if (itemsRelated(srcItem, item)) {
						let is = itemSrc.get(item)
						if (!is) {
							itemSrc.set(item, is = [])
						}
						is.push(srcItem)
						remaining.delete(srcItem)
					}
				}
			}
			// this list contains references to all source items
			// now infer the order of the source item references
			if (remaining.size == 0) {
				const refOrder = new Map()
				for (const [item, srcs] of itemSrc) {
					// XXX: hopefully multiple source items are not related to the same referencing item
					// (we need to establish explicitly relationship beforehand)
					// just take the first
					refOrder.set(item, sourceItems.indexOf(srcs[0]))
				}
				return refOrder
			}
		}
		// did not find a list of references to all source items
	}

	private static inferInterventionOrder(outcome, itvns, abstractItvns) {
		// try to find a prior sentence that refers to all of the searched interventions
		const annotsBySentence = new Map()
		for (const itvnA of abstractItvns) {
			if (itvnA.sentence.i > outcome.sentence.i || itvnA.start >= outcome.start) continue
			let list = annotsBySentence.get(itvnA.sentence.i)
			if (!list) {
				annotsBySentence.set(itvnA.sentence.i, list = [])
			}
			list.push(itvnA)
		}
		for (const list of annotsBySentence.values()) {
			// ensure the annotation objects themselves are ordered by position
			list.sort((a, b) => a.start > b.start ? 1 : 0)
		}
		// sort by last to first
		const sentenceIndexes = _.sortBy(Array.from(annotsBySentence.keys()), i => -i).map(i => annotsBySentence.get(i))
		return Extractor.inferPriorOrder(sentenceIndexes, itvns, (intervention, annot) =>
			phraseRelated(intervention.name.term, annot.terms)
		)
	}

	private static chooseIntervention(itvns, annotated: AnnotatedAbstract): Annotation {
		let weights = []
		itvns = _.sortBy(itvns, itvn => itvn.start)
		// TODO: preserve quantifier weighting again for use in choosing best intervention
		//const itvnsByQ = _.sortBy(itvns, itvn => itvn.qw ? -itvn.qw.weight : 0)
		for (let i = 0; i < itvns.length; i++) {
			const itvn = itvns[i]
			let weight = SOURCE_PRECEDENCE[itvn.source] || 0
			// quarter weighting to relative position
			weight += 0.25 * ((itvns.length - i) / itvns.length)
			// half weighting to relative quantifier weight
			//weight += 0.5 * (itvnsByQ.indexOf(itvn) / itvns.length)
			if (itvn.measures && itvn.measures.length) weight += 0.10
			if (itvn.groupn) weight += 0.10
			weights.push({ itvn, weight })
		}
		weights = _.sortBy(weights, w => -w.weight)
		return weights[0] && weights[0].itvn
	}

	private static chooseOutcome(outcomes, annotated: AnnotatedAbstract): Annotation {
		let weights = []
		outcomes = _.sortBy(outcomes, outcome => outcome.start)
		for (let i = 0; i < outcomes.length; i++) {
			const outcome = outcomes[i]
			let weight = SOURCE_PRECEDENCE[outcome.source] || 0
			// quarter weighting to relative position
			weight += 0.25 * ((outcomes.length - i) / outcomes.length)
			// weight related outcome values
			if (outcome.measures && outcome.measures.length) weight += 0.10 * outcome.measures.length
			weights.push({ outcome, weight })
		}
		weights = _.sortBy(weights, w => -w.weight)
		return weights[0] && weights[0].outcome
	}
}